/**
 * MediaPreviewLightbox — fullscreen media preview for modifier option thumbnails.
 *
 * Rendered via createPortal to document.body to avoid CSS transform stacking
 * context issues from the parent Radix UI Dialog.
 *
 * BUG GUARD — Radix DismissableLayer listens for pointerdown on document.
 * stopPropagation on every pointer event in the lightbox prevents Radix from
 * seeing those events and closing the Dialog behind the lightbox.
 * ESC is intercepted in capture phase so the lightbox closes, not the Dialog.
 * DialogContent also guards onPointerDownOutside/onEscapeKeyDown (see ProductCard.js).
 */
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

export function MediaPreviewLightbox({ url, type, onClose }) {
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      e.preventDefault();
      onCloseRef.current();
    };
    document.addEventListener("keydown", handler, { capture: true });
    return () => document.removeEventListener("keydown", handler, { capture: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!url) return null;

  const handleClose = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onCloseRef.current();
  };

  const stopPointer = (e) => e.stopPropagation();

  return createPortal(
    <div
      role="presentation"
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex: 10000, backgroundColor: "rgba(0,0,0,0.93)" }}
      onClick={handleClose}
      onPointerDown={stopPointer}
      onPointerUp={stopPointer}
    >
      {/* Close button — explicit z-index ensures it paints above the media container */}
      <button
        type="button"
        aria-label="Fechar"
        className="absolute top-4 right-4 w-12 h-12 rounded-full flex items-center justify-center"
        style={{
          zIndex: 10002,
          backgroundColor: "rgba(255,255,255,0.2)",
          color: "#fff",
          pointerEvents: "auto",
          touchAction: "manipulation",
          cursor: "pointer",
        }}
        onClick={handleClose}
        onTouchStart={handleClose}
        onPointerDown={stopPointer}
      >
        <X className="w-6 h-6" />
      </button>

      {/* Media container — click stops propagation so only backdrop click closes */}
      <div
        style={{ position: "relative", zIndex: 10001, width: "100%", maxWidth: 640, margin: "0 16px" }}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={stopPointer}
      >
        {type === "video" ? (
          <video
            src={url}
            controls
            autoPlay
            playsInline
            className="w-full rounded-2xl shadow-2xl"
            style={{ maxHeight: "80vh", objectFit: "contain", display: "block" }}
          />
        ) : (
          <img
            src={url}
            alt=""
            className="w-full rounded-2xl shadow-2xl"
            style={{ maxHeight: "80vh", objectFit: "contain", display: "block" }}
          />
        )}
      </div>
    </div>,
    document.body
  );
}
